import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { Point } from '@bre-ticketing/seatmap/core';
import type { ApiError, ManifestLayout } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, statusTone } from '../ui';
import { GenerateRows, NewGaArea, NewLevel, NewSection, NewTable } from './manifest/forms';
import { ImportSvg } from './manifest/ImportSvg';
import { SeatMapPanel } from './manifest/SeatMapPanel';

/**
 * One manifest: the map, its structure (levels, sections, rows, tables, GA
 * areas) and the lifecycle actions.
 *
 * Structural editing disappears once the layout is published rather than
 * failing on click — a published layout is frozen, and the way forward is a new
 * version. Seat attributes stay correctable (the map panel says so).
 */

type Form =
  | { kind: 'none' }
  | { kind: 'import' }
  | { kind: 'level' }
  | { kind: 'section' }
  | { kind: 'ga' }
  | { kind: 'rows'; sectionId: string }
  | { kind: 'table'; sectionId: string };

function Stat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className="tabular mt-1 text-3xl font-bold text-ink-900">{value}</p>
      <p className="mt-1 text-sm text-ink-600">{detail}</p>
    </div>
  );
}

export default function ManifestPage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>({ kind: 'none' });
  const [placing, setPlacing] = useState(false);
  const [placement, setPlacement] = useState<Point | null>(null);

  const layout = useQuery<ManifestLayout, ApiError>({
    queryKey: ['manifest-layout', id],
    queryFn: () => endpoints.manifestLayout(id),
  });

  /**
   * Everything this page knows about the room, not just the map (D-289).
   *
   * This invalidated `manifest-layout` alone, so moving a seat redrew the map
   * and left the panel's other two queries holding what they had read on
   * mount: **Undo stayed grayed out after a change that was plainly undoable**,
   * and the problem list went on reporting faults that had just been fixed.
   * Both read correct until you notice they never move.
   *
   * By prefix rather than by a list of keys, because a list is a thing to
   * forget: `manifest-edits` and `manifest-lint` were added to the panel long
   * after this line was written, and the next panel query would have been just
   * as stale.
   */
  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [name, key] = query.queryKey as [unknown, unknown];
          return typeof name === 'string' && name.startsWith('manifest') && key === id;
        },
      }),
    [queryClient, id],
  );
  const onError = useCallback((cause: ApiError) => setError(cause.message), []);
  const done = () => {
    setForm({ kind: 'none' });
    setPlacing(false);
    setError(null);
    void refresh();
  };
  const onRequestPlacement = useCallback((active: boolean) => {
    setPlacing(active);
    if (active) setPlacement(null);
  }, []);

  const mutation = (fn: () => Promise<unknown>) =>
    useMutation({
      mutationFn: () => fn(),
      onSuccess: () => {
        setError(null);
        void refresh();
      },
      onError,
    });

  const publish = mutation(() => endpoints.publishManifest(id));
  const archive = useMutation({
    mutationFn: (reason: string) => endpoints.archiveManifest(id, reason),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError,
  });
  const newVersion = useMutation({
    mutationFn: () => endpoints.newManifestVersion(id),
    onSuccess: async (result) => {
      setError(null);
      await navigate(`/manifests/${result.manifest.id}`);
    },
    onError,
  });
  const removeSection = useMutation({ mutationFn: (sectionId: string) => endpoints.deleteSection(id, sectionId), onSuccess: done, onError });
  /**
   * Copy a section with everything in it (D-298).
   *
   * Four identical boxes get drawn four times. The offset is asked for because
   * a copy landing exactly on top of the original looks like nothing happened
   * and leaves two seats in every position -- which the linter would then
   * report as stacked (D-274), correctly and unhelpfully.
   */
  const duplicateSection = useMutation({
    mutationFn: ({ sectionId, code, name, dx, dy }: { sectionId: string; code: string; name: string; dx: number; dy: number }) =>
      endpoints.copySection(id, sectionId, { code, name, dx, dy }),
    onSuccess: (result) => {
      setError(null);
      void refresh();
      window.alert(`Copied: ${result.seats} seat${result.seats === 1 ? '' : 's'} in ${result.rows} row${result.rows === 1 ? '' : 's'}. Undo takes the whole copy back.`);
    },
    onError,
  });
  const askCopy = (section: { id: string; code: string; name: string }) => {
    const code = window.prompt(`A code for the copy of ${section.code}`, `${section.code}-COPY`);
    if (!code?.trim()) return;
    const name = window.prompt('And a name', `${section.name} (copy)`);
    if (!name?.trim()) return;
    const across = window.prompt('How far to the right? (0 to keep the same x)', '400');
    if (across === null) return;
    const down = window.prompt('And how far down?', '0');
    if (down === null) return;
    duplicateSection.mutate({ sectionId: section.id, code: code.trim(), name: name.trim(), dx: Number(across) || 0, dy: Number(down) || 0 });
  };
  const removeLevel = useMutation({ mutationFn: (levelId: string) => endpoints.deleteLevel(id, levelId), onSuccess: done, onError });
  // A level can be renamed and moved in the order (D-109). The order matters:
  // it is what makes a floor seat better than a balcony seat (D-108).
  const editLevel = useMutation({
    mutationFn: ({ levelId, patch }: { levelId: string; patch: { name?: string; sort_order?: number } }) => endpoints.updateLevel(id, levelId, patch),
    onSuccess: done,
    onError,
  });
  const removeRow = useMutation({ mutationFn: (rowId: string) => endpoints.deleteRow(id, rowId), onSuccess: done, onError });
  const removeTable = useMutation({ mutationFn: (tableId: string) => endpoints.deleteTable(id, tableId), onSuccess: done, onError });
  const removeGa = useMutation({ mutationFn: (areaId: string) => endpoints.deleteGaArea(id, areaId), onSuccess: done, onError });
  const moveSection = useMutation({
    mutationFn: ({ sectionId, levelId }: { sectionId: string; levelId: string | null }) =>
      endpoints.updateSection(id, sectionId, { level_id: levelId }),
    onSuccess: done,
    onError,
  });
  // Renaming works on a published manifest (D-115): the lock triggers have
  // always let a name through and only ever guarded code, type and capacity.
  const renameSection = useMutation({
    mutationFn: ({ sectionId, name }: { sectionId: string; name: string }) => endpoints.updateSection(id, sectionId, { name }),
    onSuccess: done,
    onError,
  });
  const renameArea = useMutation({
    mutationFn: ({ areaId, name }: { areaId: string; name: string }) => endpoints.updateGaArea(id, areaId, { name }),
    onSuccess: done,
    onError,
  });
  /*
   * How many a GA area holds (D-207).
   *
   * The API has always allowed this and the page never offered it: the table
   * printed a capacity with a Rename button beside it, so the only way to fix
   * a number was to delete the area and build it again.
   *
   * **Draft only**, which is not this page being cautious — `capacity` is one
   * of the columns `ga_areas_lock_identity` guards, because a published room
   * has inventory counted against that number and tickets sold out of it.
   */
  const editCapacity = useMutation({
    mutationFn: ({ areaId, capacity, accessible }: { areaId: string; capacity: number; accessible: number }) =>
      endpoints.updateGaArea(id, areaId, { capacity, accessible_capacity: accessible }),
    onSuccess: done,
    onError,
  });
  // Taking the drawing off is allowed wherever replacing it is, because a
  // background is presentation and nothing sold depends on it (D-182).
  const clearBackground = useMutation({ mutationFn: () => endpoints.clearBackground(id), onSuccess: done, onError });
  const askName = (what: string, current: string, save: (name: string) => void) => {
    const next = window.prompt(`What should ${what} be called?`, current);
    if (next && next.trim() && next.trim() !== current) save(next.trim());
  };

  /**
   * Both numbers together, because the API rejects an accessible count above
   * the capacity — asking for them one at a time would make a perfectly
   * reasonable pair of edits fail halfway depending on which order they were
   * typed in.
   */
  const askCapacity = (area: { id: string; name: string; capacity: number; accessible_capacity: number }) => {
    const total = window.prompt(`How many does ${area.name} hold?`, String(area.capacity));
    if (total === null) return;
    const capacity = Number(total.replace(/\D/g, ''));
    if (!Number.isInteger(capacity) || capacity < 1) {
      setError('A GA area has to hold at least one person.');
      return;
    }
    const wheelchair = window.prompt(`How many of those ${capacity} are accessible spaces?`, String(area.accessible_capacity));
    if (wheelchair === null) return;
    const accessible = Number(wheelchair.replace(/\D/g, '')) || 0;
    if (accessible > capacity) {
      setError('Accessible spaces cannot be more than the capacity.');
      return;
    }
    if (capacity === area.capacity && accessible === area.accessible_capacity) return;
    editCapacity.mutate({ areaId: area.id, capacity, accessible });
  };

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  if (layout.error) return <ErrorNote>{layout.error.message}</ErrorNote>;

  const data = layout.data;
  const manifest = data?.manifest;
  const isDraft = manifest?.status === 'DRAFT';
  const canEdit = isDraft && can('manifests.edit');
  const canEditAttributes = manifest?.status !== 'ARCHIVED' && can('manifests.edit');
  const populated = (data?.sections.length ?? 0) + (data?.levels.length ?? 0) + (data?.ga_areas.length ?? 0) > 0;

  const rowsBySection = new Map<string, ManifestLayout['rows']>();
  for (const row of data?.rows ?? []) rowsBySection.set(row.section_id, [...(rowsBySection.get(row.section_id) ?? []), row]);
  const tablesBySection = new Map<string, ManifestLayout['tables']>();
  for (const table of data?.tables ?? []) tablesBySection.set(table.section_id, [...(tablesBySection.get(table.section_id) ?? []), table]);
  const seatsBySection = new Map<string, number>();
  for (const seat of data?.seats ?? []) seatsBySection.set(seat.section_id, (seatsBySection.get(seat.section_id) ?? 0) + 1);
  const sectionName = (sectionId: string) => data?.sections.find((s) => s.id === sectionId)?.name ?? '';

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <div className="flex flex-wrap items-baseline gap-3">
        {manifest ? (
          <Link to={`/venues/${manifest.venue_id}`} className="text-sm text-accent underline-offset-2 hover:underline">
            ← Venue
          </Link>
        ) : null}
        <h1 className="text-lg font-semibold text-ink-900">{manifest?.name ?? '…'}</h1>
        <span className="text-sm text-ink-400">{manifest?.code}</span>
        {manifest ? <span className="tabular text-sm text-ink-400">v{manifest.version}</span> : null}
        {manifest ? <Badge tone={statusTone(manifest.status)}>{manifest.status}</Badge> : null}
      </div>

      <div className="flex flex-wrap gap-4">
        <Stat
          label="Reserved seats"
          value={layout.isPending ? '—' : String(data?.capacity.seats ?? 0)}
          detail={`${data?.sections.length ?? 0} section(s), ${data?.rows.length ?? 0} row(s), ${data?.tables.length ?? 0} table(s)`}
        />
        <Stat label="GA capacity" value={layout.isPending ? '—' : String(data?.capacity.ga_capacity ?? 0)} detail={`${data?.ga_areas.length ?? 0} area(s)`} />
        <div className="flex flex-1 flex-col justify-center gap-2 rounded-lg border border-line bg-surface px-5 py-4">
          {isDraft ? (
            <>
              <Button variant="primary" disabled={!can('manifests.publish') || publish.isPending} onClick={() => publish.mutate()}>
                {publish.isPending ? 'Publishing…' : 'Publish this layout'}
              </Button>
              <p className="text-xs text-ink-400">Publishing freezes it. After that, changes mean a new version.</p>
            </>
          ) : (
            <>
              <Button variant="primary" disabled={!can('manifests.edit') || newVersion.isPending} onClick={() => newVersion.mutate()}>
                {newVersion.isPending ? 'Copying…' : 'Start a new version'}
              </Button>
              {manifest?.status === 'PUBLISHED' && can('manifests.publish') ? (
                <Button
                  variant="danger"
                  onClick={() => {
                    const reason = window.prompt('Why is this layout being archived?');
                    if (reason) archive.mutate(reason);
                  }}
                >
                  Archive
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {!isDraft && !layout.isPending ? (
        <p className="rounded border border-line bg-surface px-3 py-2 text-sm text-ink-600">
          This layout is {manifest?.status.toLowerCase()} and its structure cannot change. Everything sold against it points at this version, so
          re-drawing the room means starting a new one. Seat attributes and positions can still be corrected.
        </p>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" onClick={() => setForm({ kind: 'import' })}>
            Import SVG drawing
          </Button>
          <Button onClick={() => setForm({ kind: 'level' })}>Add a level</Button>
          <Button onClick={() => setForm({ kind: 'section' })}>Add a section</Button>
          <Button onClick={() => setForm({ kind: 'ga' })}>Add GA capacity</Button>
        </div>
      ) : null}

      {/*
        The room on one sheet, for the door (D-294). Outside the edit block on
        purpose: a published room is exactly the one somebody needs to print,
        and this changes nothing about it.
      */}
      <div className="mt-3">
        <Button onClick={() => window.open(`/api/admin/manifests/${id}/usher-map.pdf`, '_blank', 'noopener')}>Seating plan for ushers (PDF)</Button>
      </div>

      {/* Offered only when there is one to take off, and separately from the
          structure actions because it changes the picture and nothing else
          (D-182). It survives publishing for the same reason replacing it
          does. */}
      {canEditAttributes && manifest?.background_url ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="danger"
            disabled={clearBackground.isPending}
            onClick={() => {
              if (window.confirm('Remove the background drawing from this map?\n\nThe seats, sections and tables stay exactly as they are — only the picture behind them goes. You can import another drawing afterward.')) {
                clearBackground.mutate();
              }
            }}
          >
            {clearBackground.isPending ? 'Removing…' : 'Remove background drawing'}
          </Button>
        </div>
      ) : null}

      {canEdit && form.kind === 'import' ? (
        <ImportSvg manifestId={id} populated={populated} onCancel={() => setForm({ kind: 'none' })} onDone={done} onError={onError} />
      ) : null}
      {canEdit && form.kind === 'level' ? <NewLevel manifestId={id} onCancel={() => setForm({ kind: 'none' })} onDone={done} onError={onError} /> : null}
      {canEdit && form.kind === 'section' ? (
        <NewSection manifestId={id} levels={data?.levels ?? []} onCancel={() => setForm({ kind: 'none' })} onDone={done} onError={onError} />
      ) : null}
      {canEdit && form.kind === 'ga' ? (
        <NewGaArea manifestId={id} sections={data?.sections ?? []} onCancel={() => setForm({ kind: 'none' })} onDone={done} onError={onError} />
      ) : null}
      {canEdit && form.kind === 'rows' ? (
        <GenerateRows
          manifestId={id}
          sectionId={form.sectionId}
          sectionName={sectionName(form.sectionId)}
          placement={placement}
          onRequestPlacement={onRequestPlacement}
          onCancel={() => {
            setForm({ kind: 'none' });
            setPlacing(false);
          }}
          onDone={done}
          onError={onError}
        />
      ) : null}
      {canEdit && form.kind === 'table' ? (
        <NewTable
          manifestId={id}
          sectionId={form.sectionId}
          sectionName={sectionName(form.sectionId)}
          placement={placement}
          onRequestPlacement={onRequestPlacement}
          onCancel={() => {
            setForm({ kind: 'none' });
            setPlacing(false);
          }}
          onDone={done}
          onError={onError}
        />
      ) : null}

      {data ? (
        <SeatMapPanel
          manifestId={id}
          layout={data}
          canEditStructure={canEdit}
          canEditAttributes={canEditAttributes}
          placing={placing}
          onPlace={(point) => setPlacement(point)}
          onChanged={() => {
            setError(null);
            void refresh();
          }}
          onError={onError}
        />
      ) : null}

      <Panel title="Sections" description="Reserved seating is rows of seats or tables; generate them from a pattern rather than one at a time. A section can be renamed after the manifest is published — the name is a label; its code is not.">
        <Table headers={['Section', 'Type', 'Level', 'Rows and tables', 'Seats', '']}>
          {layout.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : (data?.sections.length ?? 0) === 0 ? (
            <EmptyRow columns={6}>No sections yet.</EmptyRow>
          ) : (
            data!.sections.map((section) => (
              <tr key={section.id} className="border-b border-line align-top last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{section.name}</div>
                  <div className="text-xs text-ink-400">{section.code}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{section.section_type.replaceAll('_', ' ').toLowerCase()}</td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {canEdit && (data?.levels.length ?? 0) > 0 ? (
                    <select
                      aria-label={`Level for ${section.name}`}
                      value={section.level_id ?? ''}
                      onChange={(event) => moveSection.mutate({ sectionId: section.id, levelId: event.target.value || null })}
                      className="rounded border border-line bg-surface px-2 py-1 text-sm"
                    >
                      <option value="">—</option>
                      {data!.levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    (data?.levels.find((level) => level.id === section.level_id)?.name ?? '—')
                  )}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  <div className="flex flex-wrap gap-1">
                    {(rowsBySection.get(section.id) ?? []).map((row) => (
                      <span key={row.id} className="inline-flex items-center gap-1 rounded border border-line bg-page px-1.5 py-0.5 text-xs">
                        {row.label}
                        {canEdit ? (
                          <button
                            type="button"
                            aria-label={`Remove row ${row.label}`}
                            className="text-ink-400 hover:text-bad"
                            onClick={() => {
                              if (window.confirm(`Remove row ${row.label} and its seats?`)) removeRow.mutate(row.id);
                            }}
                          >
                            ×
                          </button>
                        ) : null}
                      </span>
                    ))}
                    {(tablesBySection.get(section.id) ?? []).map((table) => (
                      <span key={table.id} className="inline-flex items-center gap-1 rounded border border-gold/40 bg-page px-1.5 py-0.5 text-xs">
                        table {table.label}
                        {canEdit ? (
                          <button
                            type="button"
                            aria-label={`Remove table ${table.label}`}
                            className="text-ink-400 hover:text-bad"
                            onClick={() => {
                              if (window.confirm(`Remove table ${table.label} and its seats?`)) removeTable.mutate(table.id);
                            }}
                          >
                            ×
                          </button>
                        ) : null}
                      </span>
                    ))}
                    {(rowsBySection.get(section.id)?.length ?? 0) + (tablesBySection.get(section.id)?.length ?? 0) === 0 ? <span className="text-xs text-ink-400">none</span> : null}
                  </div>
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{seatsBySection.get(section.id) ?? 0}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <div className="flex justify-end gap-2">
                    {canEditAttributes ? (
                      <Button disabled={renameSection.isPending} onClick={() => askName(`section ${section.code}`, section.name, (name) => renameSection.mutate({ sectionId: section.id, name }))}>
                        Rename
                      </Button>
                    ) : null}
                    {canEdit ? (
                      <>
                        <Button onClick={() => setForm({ kind: 'rows', sectionId: section.id })}>Add rows</Button>
                        <Button onClick={() => setForm({ kind: 'table', sectionId: section.id })}>Add table</Button>
                        <Button disabled={duplicateSection.isPending} onClick={() => askCopy(section)}>
                          Copy…
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (window.confirm(`Delete ${section.name} and all of its seats?`)) removeSection.mutate(section.id);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {(data?.levels.length ?? 0) > 0 || canEdit ? (
        <Panel
          title="Levels"
          description="Floors of the building, best first — best available works down this order, so the floor belongs above the balconies. A level with sections on it cannot be removed."
        >
          <Table headers={['Level', 'Code', 'Sections', 'Order', '']}>
            {(data?.levels.length ?? 0) === 0 ? (
              <EmptyRow columns={5}>No levels. Flat rooms do not need any.</EmptyRow>
            ) : (
              data!.levels.map((level) => {
                const count = data!.sections.filter((section) => section.level_id === level.id).length;
                return (
                  <tr key={level.id} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 font-medium text-ink-900">{level.name}</td>
                    <td className="px-5 py-3 text-xs text-ink-400">{level.code}</td>
                    <td className="tabular px-5 py-3 text-sm text-ink-600">{count}</td>
                    <td className="tabular px-5 py-3 text-sm text-ink-600">{level.sort_order}</td>
                    <td className="space-x-2 px-5 py-3 text-right whitespace-nowrap">
                      {canEditAttributes ? (
                        <>
                          <Button
                            disabled={editLevel.isPending}
                            onClick={() => askName('this level', level.name, (name) => editLevel.mutate({ levelId: level.id, patch: { name } }))}
                          >
                            Rename
                          </Button>
                          <Button
                            disabled={editLevel.isPending}
                            onClick={() => {
                              const order = window.prompt('Where does it come in the order? 0 is the best level in the house.', String(level.sort_order));
                              const next = Number(order);
                              if (order !== null && Number.isInteger(next) && next >= 0 && next !== level.sort_order) editLevel.mutate({ levelId: level.id, patch: { sort_order: next } });
                            }}
                          >
                            Reorder
                          </Button>
                        </>
                      ) : null}
                      {canEdit ? (
                        <>
                          <Button variant="danger" disabled={count > 0 || removeLevel.isPending} onClick={() => removeLevel.mutate(level.id)}>
                            Delete
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </Table>
        </Panel>
      ) : null}

      <Panel title="General admission" description="Capacity sold without a seat number. Counted as inventory the same way seats are. Capacity can be changed while the manifest is a draft; once it is published the number has inventory counted against it and tickets sold out of it, so only the name can change.">
        <Table headers={['Area', 'Kind', 'Section', 'Capacity', 'Accessible', '']}>
          {layout.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : (data?.ga_areas.length ?? 0) === 0 ? (
            <EmptyRow columns={6}>No GA areas.</EmptyRow>
          ) : (
            data!.ga_areas.map((area) => (
              <tr key={area.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{area.name}</div>
                  <div className="text-xs text-ink-400">{area.code}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{area.area_kind.replaceAll('_', ' ').toLowerCase()}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{area.section_id ? sectionName(area.section_id) : '—'}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{area.capacity}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{area.accessible_capacity}</td>
                <td className="space-x-2 px-5 py-3 text-right whitespace-nowrap">
                  {canEditAttributes ? (
                    <Button disabled={renameArea.isPending} onClick={() => askName('this area', area.name, (name) => renameArea.mutate({ areaId: area.id, name }))}>
                      Rename
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button disabled={editCapacity.isPending} onClick={() => askCapacity(area)}>
                      Capacity
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button variant="danger" onClick={() => removeGa.mutate(area.id)}>
                      Delete
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
